"use client";

import React, { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Circle, Polygon, Marker, Popup, useMapEvents } from 'react-leaflet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Save, Trash2, Edit } from 'lucide-react';
import ClientOnly from "@/components/map/ClientOnly";
import "@/components/map/leaflet-defaults";

interface Geofence {
  id: string;
  name: string;
  type: 'circle' | 'polygon';
  center_lat?: number;
  center_lng?: number;
  radius_m?: number;
  polygon_geojson?: any;
  active: boolean;
}

interface GeofenceMapProps {
  geofences: Geofence[];
  onGeofenceCreate: (geofence: Omit<Geofence, 'id'>) => void;
  onGeofenceUpdate: (id: string, geofence: Partial<Geofence>) => void;
  onGeofenceDelete: (id: string) => void;
}

function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function GeofenceMap({ 
  geofences, 
  onGeofenceCreate, 
  onGeofenceUpdate, 
  onGeofenceDelete 
}: GeofenceMapProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [creationType, setCreationType] = useState<'circle' | 'polygon'>('circle');
  const [newGeofence, setNewGeofence] = useState({
    name: '',
    center_lat: 0,
    center_lng: 0,
    radius_m: 500,
    polygon_points: [] as Array<{ lat: number; lng: number }>
  });
  const [editingGeofence, setEditingGeofence] = useState<string | null>(null);

  // Memoize initial center to avoid recalculations and potential re-renders
  const initialCenter = useMemo(() => [-23.55, -46.63] as [number, number], []);

  const handleMapClick = (lat: number, lng: number) => {
    if (!isCreating) return;

    if (creationType === 'circle') {
      setNewGeofence(prev => ({
        ...prev,
        center_lat: lat,
        center_lng: lng
      }));
    } else if (creationType === 'polygon') {
      setNewGeofence(prev => ({
        ...prev,
        polygon_points: [...prev.polygon_points, { lat, lng }]
      }));
    }
  };

  const handleCreateGeofence = () => {
    if (!newGeofence.name) return;

    const geofenceData: Omit<Geofence, 'id'> = {
      name: newGeofence.name,
      type: creationType,
      active: true
    };

    if (creationType === 'circle') {
      geofenceData.center_lat = newGeofence.center_lat;
      geofenceData.center_lng = newGeofence.center_lng;
      geofenceData.radius_m = newGeofence.radius_m;
    } else {
      geofenceData.polygon_geojson = {
        type: 'Polygon',
        coordinates: [[
          ...newGeofence.polygon_points.map(p => [p.lng, p.lat]),
          [newGeofence.polygon_points[0]?.lng, newGeofence.polygon_points[0]?.lat] // Close polygon
        ]]
      };
    }

    onGeofenceCreate(geofenceData);
    
    // Reset form
    setIsCreating(false);
    setNewGeofence({
      name: '',
      center_lat: 0,
      center_lng: 0,
      radius_m: 500,
      polygon_points: []
    });
  };

  const handleCancelCreation = () => {
    setIsCreating(false);
    setNewGeofence({
      name: '',
      center_lat: 0,
      center_lng: 0,
      radius_m: 500,
      polygon_points: []
    });
  };

  const renderGeofence = (geofence: Geofence) => {
    if (geofence.type === 'circle' && geofence.center_lat && geofence.center_lng) {
      return (
        <Circle
          key={geofence.id}
          center={[geofence.center_lat, geofence.center_lng]}
          radius={geofence.radius_m || 500}
          pathOptions={{
            color: geofence.active ? '#10b981' : '#6b7280',
            fillColor: geofence.active ? '#10b981' : '#6b7280',
            fillOpacity: 0.2
          }}
        >
          <Popup>
            <div className="p-2">
              <h3 className="font-semibold">{geofence.name}</h3>
              <p className="text-sm text-muted-foreground">
                Raio: {geofence.radius_m}m
              </p>
              <div className="flex gap-2 mt-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setEditingGeofence(geofence.id)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => onGeofenceDelete(geofence.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Popup>
        </Circle>
      );
    }

    if (geofence.type === 'polygon' && geofence.polygon_geojson) {
      const coordinates = geofence.polygon_geojson.coordinates[0];
      const positions = coordinates.slice(0, -1).map((coord: number[]) => [coord[1], coord[0]] as [number, number]);

      return (
        <Polygon
          key={geofence.id}
          positions={positions}
          pathOptions={{
            color: geofence.active ? '#10b981' : '#6b7280',
            fillColor: geofence.active ? '#10b981' : '#6b7280',
            fillOpacity: 0.2
          }}
        >
          <Popup>
            <div className="p-2">
              <h3 className="font-semibold">{geofence.name}</h3>
              <p className="text-sm text-muted-foreground">
                Polígono com {positions.length} pontos
              </p>
              <div className="flex gap-2 mt-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setEditingGeofence(geofence.id)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => onGeofenceDelete(geofence.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Popup>
        </Polygon>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Gerenciar Geocercas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isCreating ? (
            <div className="flex gap-2">
              <Button 
                onClick={() => {
                  setIsCreating(true);
                  setCreationType('circle');
                }}
              >
                Criar Círculo
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setIsCreating(true);
                  setCreationType('polygon');
                }}
              >
                Criar Polígono
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="geofence-name">Nome da Geocerca</Label>
                  <Input
                    id="geofence-name"
                    value={newGeofence.name}
                    onChange={(e) => setNewGeofence(prev => ({ 
                      ...prev, 
                      name: e.target.value 
                    }))}
                    placeholder="Ex: Depósito Central"
                  />
                </div>
                
                {creationType === 'circle' && (
                  <div className="space-y-2">
                    <Label htmlFor="radius">Raio (metros)</Label>
                    <Input
                      id="radius"
                      type="number"
                      value={newGeofence.radius_m}
                      onChange={(e) => setNewGeofence(prev => ({ 
                        ...prev, 
                        radius_m: parseInt(e.target.value) 
                      }))}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select 
                    value={creationType} 
                    onValueChange={(value: 'circle' | 'polygon') => setCreationType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="circle">Círculo</SelectItem>
                      <SelectItem value="polygon">Polígono</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                {creationType === 'circle' 
                  ? 'Clique no mapa para definir o centro do círculo'
                  : 'Clique no mapa para adicionar pontos do polígono (mínimo 3 pontos)'
                }
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleCreateGeofence}
                  disabled={
                    !newGeofence.name || 
                    (creationType === 'circle' && (!newGeofence.center_lat || !newGeofence.center_lng)) ||
                    (creationType === 'polygon' && newGeofence.polygon_points.length < 3)
                  }
                >
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Geocerca
                </Button>
                <Button variant="outline" onClick={handleCancelCreation}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map */}
      <div className="h-96 w-full rounded-lg overflow-hidden border">
        <ClientOnly>
          <MapContainer
            center={initialCenter}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            key={`geofence-map-${geofences.length}-${isCreating}-${creationType}-${newGeofence.polygon_points.length}`}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Minimal content for debugging the Context error */}
            <Marker position={initialCenter}>
              <Popup>Mapa carregado</Popup>
            </Marker>
          </MapContainer>
        </ClientOnly>
      </div>
    </div>
  );
}